// src/componentes/comunes/Toast.js
// Notificaciones efímeras sobre sonner. Se conserva la API histórica
// (useToast + ToastContainer) para no tocar a sus ~20 consumidores:
//   const { mostrarToast } = useToast();  mostrarToast('Guardado', 'success');
// El único <Toaster> vive en NotificadorGlobal (montado en App y AppMovil).
import React, { useCallback, useEffect, useId, useRef, useSyncExternalStore } from 'react';
import { Toaster, toast } from 'sonner';
import '../../estilos/toast.css';

const POR_TIPO = {
  success: toast.success,
  error: toast.error,
  info: toast.info,
  warning: toast.warning,
  advertencia: toast.warning,
};

// Registro de contenedores montados. En la app siempre hay un
// NotificadorGlobal; si falta (pruebas o pantallas aisladas), el primer
// ToastContainer montado pinta un <Toaster> de respaldo y los demás no,
// para que ningún aviso se duplique.
const registro = { globales: 0, locales: [], oyentes: new Set() };
const notificar = () => registro.oyentes.forEach((oyente) => oyente());
const suscribir = (oyente) => { registro.oyentes.add(oyente); return () => registro.oyentes.delete(oyente); };
const instantanea = () => `${registro.globales}|${registro.locales[0] || ''}`;

const PROPS_TOASTER = {
  position: 'top-right',
  closeButton: true,
  visibleToasts: 4,
  offset: 16,
  mobileOffset: { top: 'calc(64px + env(safe-area-inset-top, 0px))', left: 12, right: 12 },
  toastOptions: { className: 'ag-toast' },
  containerAriaLabel: 'Notificaciones',
};

/** Contenedor global: uno por aplicación (App y AppMovil). */
export const NotificadorGlobal = () => {
  useEffect(() => {
    registro.globales += 1; notificar();
    return () => { registro.globales -= 1; notificar(); };
  }, []);
  return <Toaster {...PROPS_TOASTER} />;
};

/** Compatibilidad: solo pinta un respaldo si no hay NotificadorGlobal. */
export const ToastContainer = () => {
  const id = useId();
  useEffect(() => {
    registro.locales.push(id); notificar();
    return () => { registro.locales = registro.locales.filter((x) => x !== id); notificar(); };
  }, [id]);
  useSyncExternalStore(suscribir, instantanea, instantanea);
  return registro.globales === 0 && registro.locales[0] === id ? <Toaster {...PROPS_TOASTER} /> : null;
};

// Hook personalizado para manejar toasts
export const useToast = () => {
  // ids emitidos por este componente, para limpiar solo los suyos
  const idsRef = useRef(new Set());

  const mostrarToast = useCallback((mensaje, tipo = 'success', duracion = 4000) => {
    const emitir = POR_TIPO[tipo] || toast;
    const id = emitir(mensaje, {
      duration: duracion > 0 ? duracion : Infinity,
      onDismiss: (t) => idsRef.current.delete(t.id),
      onAutoClose: (t) => idsRef.current.delete(t.id),
    });
    idsRef.current.add(id);
    return id;
  }, []);

  const removerToast = useCallback((id) => {
    toast.dismiss(id);
    idsRef.current.delete(id);
  }, []);

  // ✅ Limpia los toasts activos de este componente
  const limpiarToasts = useCallback(() => {
    idsRef.current.forEach((id) => toast.dismiss(id));
    idsRef.current.clear();
  }, []);

  return { toasts: [], mostrarToast, removerToast, limpiarToasts };
};
